import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useUserSettings } from "@/hooks/useUserSettings";
import { applyTheme, setupSystemThemeListener } from "@/lib/themes";
import { setAccessToken, clearAccessToken } from "@/lib/ynabService";

interface UserSettingsContextType {
  displayCurrency: string;
  theme: string;
  preferredRateSource: string | null;
  showOriginalCurrency: boolean;
  fxStalenessHours: number;
  ynabPersonalAccessToken: string | null;
  hasYnabToken: boolean;
  ynabServiceReady: boolean;
  isLoading: boolean;
  updateDisplayCurrency: (currency: string) => void;
  updateTheme: (theme: string) => void;
  updatePreferredRateSource: (source: string | null) => void;
  updateShowOriginalCurrency: (value: boolean) => void;
  updateFxStalenessHours: (hours: number) => void;
  updateYnabPersonalAccessToken: (token: string) => void;
}

const UserSettingsContext = createContext<UserSettingsContextType | undefined>(
  undefined,
);

export const useUserSettingsContext = () => {
  const context = useContext(UserSettingsContext);
  if (!context) {
    throw new Error(
      "useUserSettingsContext must be used within a UserSettingsProvider",
    );
  }
  return context;
};

interface UserSettingsProviderProps {
  children: ReactNode;
}

function parseIntervalToHours(value: unknown): number {
  if (typeof value !== "string") return 6;
  // Match "6 hours" or "6 hour"
  const m = value.match(/(\d+)\s*hour/i);
  if (m) return Math.max(1, parseInt(m[1], 10));
  // Match "HH:MM:SS"
  const m2 = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (m2) {
    const h = parseInt(m2[1], 10) || 0;
    const min = parseInt(m2[2], 10) || 0;
    const sec = (m2[3] ? parseInt(m2[3], 10) : 0) || 0;
    return Math.max(1, Math.round(h + min / 60 + sec / 3600));
  }
  return 6;
}

export const UserSettingsProvider = ({
  children,
}: UserSettingsProviderProps) => {
  const [ynabServiceReady, setYnabServiceReady] = useState(false);
  const {
    userSettings,
    isLoading,
    updateDisplayCurrency,
    updateTheme,
    updatePreferredRateSource,
    updateShowOriginalCurrency,
    updateFxStalenessHours,
    updateYnabPersonalAccessToken,
  } = useUserSettings();

  const displayCurrency = userSettings?.currency || "EUR";
  const theme = userSettings?.theme || "system";
  const preferredRateSource = (userSettings?.preferred_rate_source ?? null) as
    | string
    | null;
  const showOriginalCurrency = !!userSettings?.show_original_currency;
  const fxStalenessHours = parseIntervalToHours(
    userSettings?.fx_rate_staleness_threshold ?? "6 hours",
  );
  const ynabPersonalAccessToken =
    userSettings?.ynab_personal_access_token ?? null;
  const hasYnabToken = !!ynabPersonalAccessToken?.trim();

  // Apply theme to document
  useEffect(() => {
    applyTheme(theme as any);

    // Setup system theme listener if system theme is selected
    if (theme === "system") {
      const cleanup = setupSystemThemeListener((actualTheme) => {
        applyTheme(actualTheme);
      });
      return cleanup;
    }
  }, [theme]);

  // Initialize YNAB service with persisted token
  useEffect(() => {
    if (ynabPersonalAccessToken?.trim()) {
      console.debug(
        "[UserSettingsProvider] Initializing YNAB service with persisted token",
      );
      setAccessToken(ynabPersonalAccessToken);
      setYnabServiceReady(true);
    } else {
      console.debug("[UserSettingsProvider] Clearing YNAB service token");
      clearAccessToken();
      setYnabServiceReady(false);
    }
  }, [ynabPersonalAccessToken]);

  const contextValue: UserSettingsContextType = {
    displayCurrency,
    theme,
    preferredRateSource,
    showOriginalCurrency,
    fxStalenessHours,
    ynabPersonalAccessToken,
    hasYnabToken,
    ynabServiceReady,
    isLoading,
    updateDisplayCurrency,
    updateTheme,
    updatePreferredRateSource,
    updateShowOriginalCurrency,
    updateFxStalenessHours,
    updateYnabPersonalAccessToken,
  };

  return (
    <UserSettingsContext.Provider value={contextValue}>
      {children}
    </UserSettingsContext.Provider>
  );
};
